import React, { useState } from 'react';
import { MessageSquare, TrendingUp, Mic, Video, Hand, User, Camera, ScanFace, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import styles from './EnhancedAnalysis.module.css';
import MetricCard from './MetricCard';
import RehearsalProgress from '../RehearsalProgress/RehearsalProgress';

const VideoMetricCard = ({ title, value, icon: Icon, description }) => {

    const percentage = value <= 1 ? Math.round(value * 100) : Math.round(value);

    const getScoreColor = (val) => {
        if (val >= 80) return '#059669';
        if (val >= 60) return '#d97706';
        return '#dc2626';
    };

    const color = getScoreColor(percentage);

    return (
        <div className={styles.videoMetricCard}>
            <div className={styles.videoMetricHeader}>
                <Icon className={styles.videoMetricIcon} style={{ color }} />
                <h3 className={styles.videoMetricTitle}>{title}</h3>
            </div>
            <div className={styles.videoMetricScore}>
                <span className={styles.videoMetricValue} style={{ color }}>
                    {percentage}%
                </span>
            </div>
            <p className={styles.videoMetricDescription}>{description}</p>
        </div>
    );
};

const NoVideoMessage = () => (
    <div className={styles.noVideoMessage}>
        <Camera className={styles.noVideoIcon} />
        <h3 className={styles.noVideoTitle}>No Video Analysis Available</h3>
        <p className={styles.noVideoDescription}>
            To see video metrics for your presentation, please enable your camera when recording.
            Video analysis provides insights into your engagement, gestures, posture, and expressions.
        </p>
    </div>
);

const FollowUpQuestions = ({ questions = [] }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!questions || questions.length === 0) {
        return (
            <div className={styles.followUpCard}>
                <p>No follow-up questions available.</p>
            </div>
        );
    }

    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? questions.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev === questions.length - 1 ? 0 : prev + 1));
    };

    return (
        <div className={styles.followUpCard}>
            <div className={styles.cardHeader}>
                <HelpCircle className={styles.cardIcon} />
                <h2 className={styles.cardTitle}>Follow-up Questions</h2>
                <span className={styles.questionCounter}>
                    {currentIndex + 1}/{questions.length}
                </span>
            </div>
            <div className={styles.cardContent}>
                <button
                    className={styles.navButton}
                    onClick={handlePrevious}
                    aria-label="Previous question"
                >
                    <ChevronLeft />
                </button>
                <p className={styles.question}>{questions[currentIndex]}</p>
                <button
                    className={styles.navButton}
                    onClick={handleNext}
                    aria-label="Next question"
                >
                    <ChevronRight />
                </button>
            </div>
        </div>
    );
};

const getVideoMetricDescription = (metric, value) => {
    const percentage = value <= 1 ? Math.round(value * 100) : Math.round(value);
    switch (metric) {
        case 'engagement':
            if (percentage >= 80) return "Excellent facial engagement and eye contact.";
            if (percentage >= 60) return "Good facial engagement and eye contact.";
            return "Try to use more natural facial expressions and smile when appropriate.";

        case 'hand_gestures':
            if (percentage >= 80) return "Effective use of hand gestures.";
            if (percentage >= 60) return "Good variety and timing of gestures.";
            return "Try to keep your hands visible and use natural movements.";

        case 'posture':
            if (percentage >= 80) return "Excellent posture and body language.";
            if (percentage >= 60) return "Good posture with minor areas for improvement.";
            return "Work on maintaining a straight and professional posture.";

        case 'smile':
            if (percentage >= 80) return "You smiled most of the time.";
            if (percentage >= 60) return "Good use of smiling with appropriate variations.";
            return "Consider smiling at key moments to enhance engagement.";

        default:
            return "No description available.";
    }
};


const EnhancedAnalysis = ({ enhancedAnalysis, rehearsalData, onTabChange, videoOnlyPractice }) => {
    if (!enhancedAnalysis) return null;
    // eslint-disable-next-line
    const { environment, scores, optimal_ranges, insights } = enhancedAnalysis;
    const standardMetrics = {
        overall: scores.overall,
        ...(videoOnlyPractice ? {} : { clarity: scores.clarity, pitch: scores.pitch })
    };


    const hasVideoMetrics = scores.video !== null && typeof scores.video === 'object';

    const videoMetrics = hasVideoMetrics ? [
        {
            key: 'engagement',
            title: 'Facial Engagement',
            value: scores.video.facial_engagement_score,
            icon: ScanFace,
        },
        {
            key: 'hand_gestures',
            title: 'Hand Gestures',
            value: scores.video.hand_gestures_score,
            icon: Hand,
        },
        {
            key: 'posture',
            title: 'Posture',
            value: scores.video.posture_score / 100,
            icon: User,
        }
    ] : [];

    return (
        <div className={styles.container}>
            <div className={styles.metricsGrid}>
                {Object.entries(standardMetrics).map(([key, value]) => (
                    <MetricCard
                        key={key}
                        title={key}
                        value={value}
                        icon={key === 'pitch' ? Mic : TrendingUp}
                    />
                ))}
            </div>

            <div className={styles.videoAnalysisCard}>
                <div className={styles.videoAnalysisHeader}>
                    <Video className={styles.sectionIcon} />
                    <h2 className={styles.sectionTitle}>Video Analysis</h2>
                    {hasVideoMetrics && (
                        <span className={styles.duration}>
                            Duration: {scores.video.duration.toFixed(1)}s
                        </span>
                    )}
                </div>
                {hasVideoMetrics ? (
                    <div className={styles.videoMetricsGrid}>
                        {videoMetrics.map((metric) => (
                            <VideoMetricCard
                                key={metric.key}
                                title={metric.title}
                                value={metric.value}
                                icon={metric.icon}
                                description={getVideoMetricDescription(metric.key, metric.value)}
                            />
                        ))}
                    </div>
                ) : (
                    <NoVideoMessage />
                )}
            </div>

            <RehearsalProgress
                rehearsalData={rehearsalData}
                onTabChange={onTabChange}
            />

            <div className={styles.contentGrid}>
                <section className={styles.recommendationsSection}>
                    <div className={styles.sectionHeader}>
                        <MessageSquare className={styles.sectionIcon} />
                        <h2 className={styles.sectionTitle}>Summary</h2>
                    </div>
                    <div className={styles.recommendationsList}>
                        <div className={styles.recommendationItem}>
                            {insights.summary}
                        </div>
                    </div>
                </section>
                <section className={styles.analysisSection}>
                    <FollowUpQuestions questions={insights.follow_up_questions} />
                </section>
            </div>
            <p className={styles.environment}>Session Environment: {environment}</p>
        </div>
    );
};

export default EnhancedAnalysis;